import React from "react";
import {
  Card,
  Row,
  Col,
  Form,
  Input,
  Select,
  Checkbox,
  Radio,
  DatePicker,
} from "antd";
import dayjs from "dayjs";

const RenderCustomFields = ({
  customfields,
  dynamicOptions,
  fetchDynamicOptions,
}) => {
  if (!customfields || customfields.length === 0) return null;

  const sortedFields = [...customfields].sort((a, b) => a.sort - b.sort);
  const hasSection = sortedFields.some((f) => f.type === "Section");

  const sections = [];
  let currentSection = {
    title: hasSection ? "General" : "System Generated Fields",
    fields: [],
  };

  sortedFields.forEach((field) => {
    if (field.type === "Section") {
      if (currentSection.fields.length > 0) sections.push(currentSection);
      currentSection = { title: field.label, fields: [] };
    } else {
      currentSection.fields.push(field);
    }
  });
  if (currentSection.fields.length > 0) sections.push(currentSection);

  return (
    <>
      {sections.map((section, index) => (
        <Card key={index} title={section.title} style={{ marginBottom: 20 }}>
          <Row gutter={[24, 24]}>
            {section.fields.map((field) => {
              const colSize =
                field.col_desktop === "Small"
                  ? 6
                  : field.col_desktop === "Medium"
                  ? 12
                  : 24;

              const rules = [];

              // Required rule
              if (field.required)
                rules.push({
                  required: true,
                  message: `${field.label} is required`,
                });

              // Allow only numbers (and decimals if enabled)
              if (field.type === "Number") {
                rules.push({
                  pattern: field.allow_decimal
                    ? /^[0-9]*[.]?[0-9]*$/
                    : /^[0-9]*$/,
                  message: field.allow_decimal
                    ? "Only numbers or decimals allowed"
                    : "Only whole numbers allowed",
                });

                // ✅ Enforce min/max validation
                if (field.min_value !== null && field.min_value !== undefined)
                  rules.push({
                    validator: (_, value) =>
                      value === undefined ||
                      value === "" ||
                      Number(value) >= Number(field.min_value)
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error(`Minimum value is ${field.min_value}`)
                          ),
                  });

                if (field.max_value !== null && field.max_value !== undefined)
                  rules.push({
                    validator: (_, value) =>
                      value === undefined ||
                      value === "" ||
                      Number(value) <= Number(field.max_value)
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error(`Maximum value is ${field.max_value}`)
                          ),
                  });
              }

              return (
                <Col key={field.id} lg={colSize} md={colSize} sm={24} xs={24}>
                  <Form.Item
                    label={field.label}
                    name={`field_${field.id}`}
                    rules={rules}
                    initialValue={
                      field.type === "Date" || field.type === "Datetime"
                        ? field.default_value
                          ? dayjs(field.default_value)
                          : dayjs()
                        : ["Checkbox", "Multi"].includes(field.type)
                        ? field.default_value
                          ? field.default_value.split(",")
                          : []
                        : field.default_value || ""
                    }
                  >
                    {field.type === "Input" ? (
                      <Input
                        size="large"
                        disabled={!!field.disabled}
                        placeholder={`Enter ${field.label}`}
                        maxLength={field.character_limit || undefined}
                        prefix={field.prefix || null}
                        suffix={field.suffix || null}
                      />
                    ) : field.type === "Textarea" ? (
                      <Input.TextArea
                        rows={4}
                        disabled={!!field.disabled}
                        placeholder={`Enter ${field.label}`}
                        maxLength={field.character_limit || undefined}
                        showCount={!!field.character_limit}
                      />
                    ) : field.type === "Select" || field.type === "Multi" ? (
                      <Select
                        showSearch
                        allowClear
                        size="large"
                        placeholder="Please select"
                        mode={field.type === "Multi" ? "multiple" : undefined}
                        disabled={!!field.disabled}
                        onFocus={() =>
                          (field.data_type === "dynamic" ||
                            field.data_type === "lookups") &&
                          fetchDynamicOptions(
                            field.data_reference,
                            field.data_type
                          )
                        }
                      >
                        {field.data_type === "static" && field.data_values
                          ? field.data_values
                              .split("|")
                              .map((val) => (
                                <Select.Option key={val.trim()} value={val.trim()}>
                                  {val.trim()}
                                </Select.Option>
                              ))
                          : (field.data_type === "dynamic" ||
                              field.data_type === "lookups") &&
                            dynamicOptions[field.data_reference]?.map((opt) => (
                              <Select.Option key={opt.label} value={opt.label}>
                                {opt.label}
                              </Select.Option>
                            ))}
                      </Select>
                    ) : field.type === "Number" ? (
                      <Input
                        type="text"
                        size="large"
                        disabled={!!field.disabled}
                        placeholder={`Enter ${field.label}`}
                        min={field.min_value || undefined}
                        max={field.max_value || undefined}
                        step={
                          field.allow_decimal
                            ? field.step || "0.01"
                            : field.step || "1"
                        }
                        prefix={field.prefix || null}
                        suffix={field.suffix || null}
                        onChange={(e) => {
                          const val = e.target.value;
                          // ✅ Prevent decimal input if not allowed
                          if (!field.allow_decimal && val.includes(".")) {
                            e.target.value = val.replace(/\./g, "");
                          }
                        }}
                      />
                    ) : field.type === "Checkbox" ? (
                      <Checkbox.Group
                        disabled={!!field.disabled}
                        options={
                          field.data_values
                            ? field.data_values
                                .split("|")
                                .map((val) => ({
                                  label: val.trim(),
                                  value: val.trim(),
                                }))
                            : field.default_value
                            ? field.default_value
                                .split(",")
                                .map((val) => ({
                                  label: val.trim(),
                                  value: val.trim(),
                                }))
                            : []
                        }
                      />
                    ) : field.type === "Radio" ? (
                      <Radio.Group
                        disabled={!!field.disabled}
                        options={
                          field.data_values
                            ? field.data_values
                                .split(",")
                                .map((val) => ({
                                  label: val,
                                  value: val,
                                }))
                            : []
                        }
                      />
                    ) : field.type === "Date" ? (
                      <DatePicker
                        style={{ width: "100%" }}
                        disabled={!!field.disabled}
                        format={field.date_format || "YYYY-MM-DD"}
                        defaultValue={
                          field.default_value
                            ? dayjs(field.default_value)
                            : dayjs() // fallback default date = today
                        }
                      />
                      
                    ) : field.type === "Datetime" ? (
                      <DatePicker
                        style={{ width: "100%" }}
                        showTime
                        disabled={!!field.disabled}
                        format={field.date_format || "YYYY-MM-DD HH:mm"}
                      />
                    ) : (
                      <Input
                        size="large"
                        disabled={!!field.disabled}
                        placeholder={`Enter ${field.label}`}
                      />
                    )}
                  </Form.Item>
                </Col>
              );
            })}
          </Row>
        </Card>
      ))}
    </>
  );
};

export default RenderCustomFields;
